import React, { useEffect, useRef, useState } from 'react';
import { Alert, Form, Input, message, Modal, Pagination, Select, Space, Typography } from 'antd';
import { getId } from '../../helpers/file';
import { SaleLink, SaleLinkInput, saleRequest } from '../../services/saleLinks';
import { saleError, useSaleText } from './saleUi';

export interface SaleSource { id: number; fileName: string; storageType?: string; expireTime?: string | null }
interface Props {
  open: boolean; source?: SaleSource; link?: SaleLink; onCancel: () => void; onSaved: (link: SaleLink) => void;
}

const SaleCreateModal: React.FC<Props> = ({ open, source, link, onCancel, onSaved }) => {
  const text = useSaleText();
  const [form] = Form.useForm<SaleLinkInput>();
  const [saving, setSaving] = useState(false);
  const [sources, setSources] = useState<SaleSource[]>([]);
  const [selected, setSelected] = useState<SaleSource | undefined>(source);
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const request = useRef(0);

  useEffect(() => {
    if (!open) return;
    setSelected(source); setKeyword(''); setPage(1); setLoadError('');
    form.resetFields();
    form.setFieldsValue(link || { uploadId: source?.id, title: source?.fileName || '', description: '', contact: '', remark: '' });
  }, [open, source, link, form]);

  useEffect(() => {
    if (!open || source || link) return;
    const current = ++request.current;
    const controller = new AbortController();
    setLoading(true);
    const params = new URLSearchParams({ uid: getId() || '', dir: '__all__', page: String(page), pageSize: '20', keyword });
    void fetch(`/img/history?${params}`, { signal: controller.signal, credentials: 'same-origin' })
      .then(async (response) => {
        if (!response.ok) throw new Error('History unavailable');
        const result = await response.json();
        if (!Array.isArray(result.data)) throw new Error('History unavailable');
        if (current === request.current) { setSources(result.data); setTotal(result.total || 0); setLoadError(''); }
      }).catch((error) => { if (error.name !== 'AbortError') setLoadError(text('文件列表加载失败，请重新搜索。', 'Could not load files. Search again.')); })
      .finally(() => { if (current === request.current) setLoading(false); });
    return () => controller.abort();
  }, [open, source, link, keyword, page, text]);

  const save = async () => {
    if (saving) return;
    let values: SaleLinkInput;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const saved = await saleRequest<SaleLink>(link ? `/${link.id}` : '', link ? 'PATCH' : 'POST', values);
      message.success(text('已保存售卖链接', 'Sale link saved'));
      onSaved(saved);
    } catch (error) { message.error(saleError(error, text)); }
    finally { setSaving(false); }
  };

  return <Modal open={open} destroyOnClose title={link ? text('编辑售卖链接', 'Edit sale link') : text('创建售卖链接', 'Create sale link')}
    onCancel={onCancel} onOk={() => void save()} confirmLoading={saving} width={620} okText={text('保存', 'Save')} cancelText={text('取消', 'Cancel')}>
    <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
      {source || link ? <>
        <Typography.Paragraph>{text('资源：', 'Resource: ')}{source?.fileName || link?.fileName}</Typography.Paragraph>
        <Form.Item name="uploadId" hidden><Input /></Form.Item>
      </> : <>
        <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }}>
          <Input.Search placeholder={text('搜索已上传文件', 'Search uploaded files')} onSearch={(value) => { setKeyword(value); setPage(1); }} allowClear />
          {loadError && <Alert type="error" message={loadError} />}
        </Space>
        <Form.Item name="uploadId" label={text('选择资源', 'Choose resource')} rules={[{ required: true, message: text('请选择文件', 'Select a file') }]}>
          <Select loading={loading} placeholder={text('选择一个已上传文件', 'Choose one uploaded file')}
            options={[...(selected && !sources.some((item) => item.id === selected.id) ? [selected] : []), ...sources]
              .map((item) => ({ value: item.id, label: item.fileName }))}
            onChange={(id: number) => {
              const next = sources.find((item) => item.id === id); setSelected(next);
              if (next && !form.getFieldValue('title')) form.setFieldValue('title', next.fileName);
            }} />
        </Form.Item>
        <Pagination size="small" current={page} total={total} pageSize={20} showSizeChanger={false} onChange={setPage} />
      </>}
      <Form.Item name="title" label={text('售卖标题', 'Title')} rules={[{ required: true, whitespace: true }]}><Input maxLength={200} /></Form.Item>
      <Form.Item name="description" label={text('公开简介', 'Public introduction')}><Input.TextArea rows={4} maxLength={5000} showCount /></Form.Item>
      <Form.Item name="contact" label={text('卖家联系方式', 'Seller contact')}><Input maxLength={500} /></Form.Item>
      <Form.Item name="remark" label={text('内部备注（仅自己可见）', 'Private note')}><Input maxLength={500} /></Form.Item>
    </Form>
  </Modal>;
};
export default SaleCreateModal;
